/* eslint-disable camelcase */
import React, { createContext, useEffect, useState } from "react";
import getSymbolFromCurrency from "currency-symbol-map";
import PropTypes from "prop-types";
import { useCookies } from "react-cookie";
import { globalHistory } from "@reach/router";
import { trigger } from "~utils/analytics";
import { fancyError, fancyWarning } from "~utils/helpers";
import {
  getCheckoutURL,
  getInventoryIdByDefaultVariant,
  getInventoryLevelsByIds
} from "~utils/shopify";

export const AppContext = createContext({});

const AppProvider = ({ children }) => {
  const [activeCurrency, setActiveCurrency] = useState(`AUD`);
  const [activeCurrencySymbol, setActiveCurrencySymbol] = useState(null);
  const [cart, setCart] = useState([]);
  const [cartActive, setCartActive] = useState(false);
  const [cartItemAdded, setCartItemAdded] = useState(false);
  const [contactLink, setContactLink] = useState(null);
  const [cookies, setCookie] = useCookies(null);
  const [cookiesAccepted, setCookiesAccepted] = useState(true);
  const [cookieMessageActive, setCookieMessageActive] = useState(false);
  const [inventoryCache, setInventoryCache] = useState(null);
  const [inventoryFetched, setInventoryFetched] = useState(null);
  const [inventoryLoaded, setInventoryLoaded] = useState(null);
  const [menuActive, setMenuActive] = useState(false);
  const [pathname, setPathname] = useState(null);
  const [notificationActive, setNotificationActive] = useState(true);
  const [termsLink, setTermsLink] = useState(null);

  //

  useEffect(() => {
    if (window) {
      setPathname(window.location.pathname);
    }

    //
    // [cookie] cart

    if (!cookies?.accepted) {
      setCookieMessageActive(true);
    } else {
      setCookiesAccepted(true);

      if (cookies?.[`${process.env.GATSBY_REGION_CODE}_cart`]) {
        const parsedCart = cookies[`${process.env.GATSBY_REGION_CODE}_cart`];

        let valid =
          Array.isArray(cookies[`${process.env.GATSBY_REGION_CODE}_cart`]) &&
          cookies?.[`${process.env.GATSBY_REGION_CODE}_cart`]?.[0];

        if (valid) {
          parsedCart.forEach(cookieCartItem => {
            if (!valid) {
              return;
            }

            if (
              typeof cookieCartItem === `undefined` ||
              cookieCartItem === null ||
              cookieCartItem === false ||
              cookieCartItem === `` ||
              !cookieCartItem?.variantId ||
              !cookieCartItem?.quantity
            ) {
              valid = false;
            }
          });
        }

        if (!valid || process.env.GATSBY_RESET_COOKIES) {
          fancyWarning(`Resetting cart data`);
          setCookie([`${process.env.GATSBY_REGION_CODE}_cart`], [], {
            path: `/`
          });
          setCart([]);
        } else {
          setCart(parsedCart);
        }
      }

      if (cookies?.currency) {
        setActiveCurrency(cookies.currency);
      }
    }

    //

    return globalHistory.listen(({ location }) => {
      setPathname(location.pathname);
    });
  }, []);

  //

  useEffect(() => {
    if (cartItemAdded) {
      setTimeout(() => {
        setCartItemAdded(false);
      }, 2000);
    }
  }, [cartItemAdded]);

  // [cookie] acceptance

  useEffect(() => {
    if (cookiesAccepted) {
      setCookie(`accepted`, true, { path: `/` });
    }
  }, [cookiesAccepted]);

  // [cookie] currency

  useEffect(() => {
    if (cookiesAccepted) {
      setCookie(`${process.env.GATSBY_REGION_CODE}_currency`, activeCurrency, {
        path: `/`
      });
    }

    setActiveCurrencySymbol(getSymbolFromCurrency(activeCurrency));
  }, [activeCurrency, cookiesAccepted]);

  //

  const addToCart = (productWithVariant, quantity = 1) => {
    if (!productWithVariant?.variant) {
      return;
    }

    let existingCartPosition = null;

    const cartClone = JSON.parse(JSON.stringify(cart));

    cartClone.forEach((cartItem, cartIndex) => {
      if (existingCartPosition !== null) {
        return;
      }

      if (cartItem.variantId === productWithVariant.variant.id) {
        existingCartPosition = cartIndex;
      }
    });

    if (existingCartPosition === null) {
      cartClone.push({
        quantity,
        variantId: productWithVariant.variant.id
      });
    } else {
      cartClone[existingCartPosition].quantity += quantity;
    }

    setCartActive(true);
    setCartItemAdded(true);
    setCart(cartClone);

    trigger([`fb`, `ga`], `add_to_cart`, {
      product: productWithVariant,
      quantity
    });

    if (cookiesAccepted) {
      setCookie(`${process.env.GATSBY_REGION_CODE}_cart`, cartClone, {
        path: `/`
      });
    }
  };

  const checkout = products => {
    if (
      !process.env.GATSBY_SHOPIFY_STORE ||
      process.env.GATSBY_SHOPIFY_STORE === ``
    ) {
      fancyError(`Shopify environment variables have not been defined.`);

      return;
    }

    setCartActive(false);

    //

    trigger([`fb`, `ga`], `begin_checkout`, {
      cart,
      products
    });

    //

    getCheckoutURL(cart, activeCurrency).then(response => {
      response.json().then(({ data }) => {
        if (data?.checkoutCreate?.checkout?.webUrl) {
          let { webUrl } = data.checkoutCreate.checkout;

          if (webUrl.includes(`joey-scandizzo-online.myshopify.com`)) {
            webUrl = webUrl.replace(
              `joey-scandizzo-online.myshopify.com`,
              `store.joeyscandizzo.com.au`
            );
          }

          window.location.href = webUrl;
        }
      });
    });
  };

  //

  const decreaseQuantityByCartIndex = (cartIndex, product) => {
    if (!cart?.[cartIndex]) {
      return;
    }

    const cartClone = JSON.parse(JSON.stringify(cart));

    if (cartClone[cartIndex].quantity <= 1) {
      cartClone.splice(cartIndex, 1);
    } else {
      cartClone[cartIndex].quantity -= 1;
    }

    setCart(cartClone);

    trigger([`ga`], `remove_from_cart`, {
      product
    });

    if (cookiesAccepted) {
      setCookie([`${process.env.GATSBY_REGION_CODE}_cart`], cartClone, {
        path: `/`
      });
    }
  };

  const increaseQuantityByCartIndex = (cartIndex, product) => {
    if (!cart?.[cartIndex]) {
      return;
    }

    const cartClone = JSON.parse(JSON.stringify(cart));

    cartClone[cartIndex].quantity += 1;

    setCart(cartClone);

    //

    trigger([`fb`, `ga`], `add_to_cart`, {
      product
    });

    if (cookiesAccepted) {
      setCookie([`${process.env.GATSBY_REGION_CODE}_cart`], cartClone, {
        path: `/`
      });
    }
  };

  const removeFromCartByIndex = (cartIndex, product) => {
    if (!cart?.[cartIndex]) {
      return;
    }

    trigger([`ga`], `remove_from_cart`, {
      product,
      quantity: cart[cartIndex].quantity
    });

    //

    const cartClone = JSON.parse(JSON.stringify(cart));

    cartClone.splice(cartIndex, 1);

    setCart(cartClone);

    //

    if (cookiesAccepted) {
      setCookie([`${process.env.GATSBY_REGION_CODE}_cart`], cartClone, {
        path: `/`
      });
    }
  };

  const refreshInventoryCache = async (
    shopifyProducts,
    shopifyAdminProducts
  ) => {
    if (
      typeof window === `undefined` ||
      typeof fetch === `undefined` ||
      window.location.href.includes(`localhost`) ||
      window.location.href.includes(`192.168`)
    ) {
      if (!inventoryCache) {
        setInventoryCache({});
      }

      return;
    }

    const variantsByInventoryId = {};

    shopifyProducts.edges.forEach(({ node }) => {
      if (node?.variants?.[0]) {
        node.variants.forEach(variant => {
          const inventoryId = getInventoryIdByDefaultVariant(
            shopifyAdminProducts.edges,
            node.handle
          );

          if (inventoryId && inventoryId !== ``) {
            variantsByInventoryId[inventoryId] = variant;
          }
        });
      }
    });

    //

    const saveInventoryBatch = async response => {
      return new Promise((resolve, reject) => {
        const inventoryBatch = {};

        response.json().then(inventory => {
          if (!inventory?.inventory_levels?.[0]) {
            reject(new Error(`Couldn't find inventory levels`));
            return;
          }

          inventory.inventory_levels.forEach(inventoryLevel => {
            const variant =
              variantsByInventoryId[inventoryLevel.inventory_item_id];

            if (typeof inventoryLevel.available === `undefined`) {
              inventoryBatch[variant.id] = 0;

              return;
            }

            inventoryBatch[variant.id] = inventoryLevel.available;
          });

          resolve(inventoryBatch);
        });
      });
    };

    getInventoryLevelsByIds(Object.keys(variantsByInventoryId)).then(
      async responses => {
        let newInventoryCache = {};

        await Promise.all(
          responses.map(async response => {
            const batchedInventory = await saveInventoryBatch(
              response,
              variantsByInventoryId
            );

            newInventoryCache = Object.assign(
              newInventoryCache,
              batchedInventory
            );
          })
        );

        setInventoryCache(newInventoryCache);
      }
    );
  };

  //

  useEffect(() => {
    if (inventoryCache) {
      setInventoryFetched(true);
    }
  }, [inventoryCache]);

  //

  return (
    <AppContext.Provider
      value={{
        cart,
        setCart,
        cartActive,
        setCartActive,
        cartItemAdded,
        setCartItemAdded,
        activeCurrencySymbol,
        setActiveCurrencySymbol,
        activeCurrency,
        setActiveCurrency,
        contactLink,
        setContactLink,
        cookiesAccepted,
        setCookiesAccepted,
        cookieMessageActive,
        setCookieMessageActive,
        inventoryCache,
        setInventoryCache,
        refreshInventoryCache,
        inventoryFetched,
        setInventoryFetched,
        inventoryLoaded,
        setInventoryLoaded,
        menuActive,
        setMenuActive,
        notificationActive,
        setNotificationActive,
        pathname,
        termsLink,
        setTermsLink,
        //
        addToCart,
        checkout,
        decreaseQuantityByCartIndex,
        increaseQuantityByCartIndex,
        removeFromCartByIndex
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

AppProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AppProvider;
